import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

/** ラベル（API6207 の1要素 / 記事のラベル表示に共用） */
export type LabelResult = {
  labelId: string;
  name: string;
  /** HEX 色コード（#RRGGBB / #RRGGBBAA） */
  color: string;
};

export type CreateLabelParams = {
  name: string;
  color: string;
};

export type UpdateLabelParams = {
  labelId: string;
  name: string;
  color: string;
};

export type DeleteLabelParams = {
  labelId: string;
};

/**
 * セッションIDヘッダー（未ログイン可APIでも session があれば付与する）。
 * ゲスト時は null になるが、公開APIは未認証で通る。
 */
const sessionHeaders = () => ({
  headers: {
    'X-Session-Id': localStorage.getItem('session-id'),
  },
});

// --- API関数 ---

/**
 * ラベル一覧取得API（API6207 / 未ログイン可）
 * 有効な（論理削除されていない）ラベルを全件取得する。
 */
export const listLabel = async (): Promise<LabelResult[]> => {
  try {
    const response = await axios.get(KanzhongBackend.LIST_LABEL, sessionHeaders());
    // API6207 のレスポンスは results（ラベル配列）
    return response.data.results ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ラベル作成API（API6208 / 認証のみ）
 * 論理削除済み同名は復活して再利用する。有効な同名が既存なら 422。
 * @param params.name - ラベル名（必須, 最大64文字）
 * @param params.color - HEX 色コード（必須, 最大9文字。形式バリデーションはサーバでは行わない）
 * @returns 作成 / 復活した labelId
 */
export const createLabel = async (params: CreateLabelParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_LABEL, params, sessionHeaders());
    return response.data.labelId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ラベル更新API（API6209 / 認証のみ）
 * @param params.labelId - ラベルID（必須, 最大64文字）
 * @param params.name - ラベル名（必須, 最大64文字）
 * @param params.color - HEX 色コード（必須, 最大9文字）
 * @returns 対象の labelId
 */
export const updateLabel = async (params: UpdateLabelParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.UPDATE_LABEL, params, sessionHeaders());
    return response.data.labelId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ラベル削除API（API6210 / 認証のみ）
 * 論理削除する。設定済み記事の参照は残す。
 * @param params.labelId - ラベルID（必須, 最大64文字）
 * @returns 削除した labelId
 */
export const deleteLabel = async (params: DeleteLabelParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.DELETE_LABEL, params, sessionHeaders());
    return response.data.labelId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
